/**
 * Image Preloader Utility
 * Preloads images for better performance
 */

// Critical images that should be preloaded immediately
const CRITICAL_IMAGES = [
  '/assets/img/logo.png',
];

// Preload a single image
export const preloadImage = (src, options = {}) => {
  return new Promise((resolve, reject) => {
    // Create link preload for better browser optimization
    if (typeof document !== 'undefined' && options.priority === 'high') {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = src;
      link.setAttribute('fetchpriority', 'high');
      document.head.appendChild(link);
    }
    
    const img = new Image();
    
    // Set priority attributes
    if (options.priority === 'high') {
      img.fetchPriority = 'high';
      img.loading = 'eager';
    } else {
      img.loading = 'lazy';
      img.fetchPriority = 'auto';
    }
    
    img.onload = () => {
      resolve(img);
    };
    img.onerror = () => {
      reject(new Error(`Failed to load image: ${src}`));
    };
    
    // Set src to start loading
    img.src = src;
    
    // If image is already cached, resolve immediately
    if (img.complete && img.naturalWidth !== 0) {
      resolve(img);
    }
  });
};

// Preload multiple images
export const preloadImages = (imageSources, options = {}) => {
  // For high priority, load first few immediately, then stagger the rest
  if (options.priority === 'high' && imageSources.length > 3) {
    // Load first 3 immediately
    imageSources.slice(0, 3).forEach(src => {
      preloadImage(src, options).catch(() => {});
    });
    
    // Stagger the rest
    const remaining = imageSources.slice(3);
    remaining.forEach((src, index) => {
      setTimeout(() => {
        preloadImage(src, options).catch(() => {});
      }, index * (options.staggerDelay || 50));
    });
    
    return Promise.resolve();
  }
  
  const promises = imageSources.map((src, index) => {
    // Stagger loading for non-critical images
    const delay = options.stagger ? index * (options.staggerDelay || 50) : 0;
    
    return new Promise((resolve) => {
      setTimeout(() => {
        preloadImage(src, options)
          .then(resolve)
          .catch(() => resolve(null)); // Don't fail all if one fails
      }, delay);
    });
  });
  
  return Promise.all(promises);
};

// Preload critical images immediately
export const preloadCriticalImages = () => {
  if (typeof window === 'undefined') return;
  
  CRITICAL_IMAGES.forEach(src => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = src;
    link.setAttribute('fetchpriority', 'high');
    document.head.appendChild(link);
    
    // Also preload using Image object
    preloadImage(src, { priority: 'high' }).catch(() => {});
  });
};

// Preload images when they're about to be viewed (within viewport + margin)
export const preloadImagesInViewport = (imageSources, margin = 500) => {
  if (typeof window === 'undefined' || !imageSources.length) return;
  
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const imgSrc = entry.target.dataset.src;
          if (imgSrc) {
            preloadImage(imgSrc, { priority: 'auto' }).catch(() => {});
            observer.unobserve(entry.target);
          }
        }
      });
    },
    {
      rootMargin: `${margin}px`,
      threshold: 0.01,
    }
  );
  
  // Create placeholder elements for images not yet in DOM
  imageSources.forEach(src => {
    const placeholder = document.createElement('div');
    placeholder.dataset.src = src;
    placeholder.style.position = 'absolute';
    placeholder.style.visibility = 'hidden';
    placeholder.style.width = '1px';
    placeholder.style.height = '1px';
    document.body.appendChild(placeholder);
    observer.observe(placeholder);
  });
  
  return () => {
    observer.disconnect();
  };
};

// Preload images for a specific route/page
export const preloadRouteImages = (route) => {
  const routeImageMap = {
    '/': [
      // Homepage images - preload first few service images
      'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200&h=800&fit=crop&q=80',
      'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&h=800&fit=crop&q=80',
      'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=1200&h=800&fit=crop&q=80',
    ],
    '/services': [
      // First 4 service images
      'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200&h=800&fit=crop&q=80',
      'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&h=800&fit=crop&q=80',
      'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=1200&h=800&fit=crop&q=80',
      'https://images.unsplash.com/photo-1561070791-2526d30994b5?w=1200&h=800&fit=crop&q=80',
    ],
    '/news': [
      // First 3 news images
      '/assets/img/News/Sri Lanka.jfif',
      '/assets/img/News/TRade Mission 1.jfif',
      '/assets/img/News/aArohan.jfif',
    ],
  };
  
  const images = routeImageMap[route] || [];
  if (images.length > 0) {
    preloadImages(images.slice(0, 4), { 
      priority: 'high',
      stagger: true,
      staggerDelay: 100 
    }).catch(() => {});
  }
};

// Initialize preloading on app start
if (typeof window !== 'undefined') {
  // Preload critical images immediately
  preloadCriticalImages();
  
  // Preload images for current route
  const currentPath = window.location.pathname;
  preloadRouteImages(currentPath);
}

